import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CompanySectionLayout from '../../../../component/B2B/CompanySectionLayout';
import Loader from '../../../../component/common/Loader';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import { fetchPerksAvailability } from './companyPerksApi';

/**
 * Shell for the COMPANY panel's Perks section — the umbrella over Instant
 * Support (EAP), Salary Sacrifice, Health Scheme and Custom Perks, which the
 * super-admin panel groups under Offerings.
 *
 * Title, mode badge, page dropdown and department filter all come from
 * CompanySectionLayout, shared with Sleep, Breathwork, Habit Tracker and
 * Marketplace.
 *
 * WHAT MAKES THIS ONE DIFFERENT: the tab list is not fixed. Perks is the only
 * section whose sub-pages depend on what the company has bought, so the layout
 * asks the backend first (`/perks-hub/availability`) and builds the dropdown
 * from the answer:
 *
 *   Salary Sacrifice → company.salarySacrificeEnable
 *   Health Scheme    → company.healthSchemeEnable
 *   Instant Support  → an approved InstantSupport plan exists
 *   Custom Perks     → the company has added at least one
 *
 * A company with none of them never reaches this layout: it is bounced to the
 * dashboard. Hiding a tab is not access control, though — every endpoint
 * behind these pages enforces the same check and 403s independently.
 */

const CompanyPerksLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isB2BFansMode } = useContext(SidebarContext);
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchPerksAvailability().then((data) => {
      if (!mounted) return;
      setAvailability(data);
      setLoading(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const population = isB2BFansMode ? 'members' : 'employees';
  const base = `/${slug}/perks`;

  const pages = [
    {
      label: 'Overview',
      path: base,
      description: 'Every perk you run, and how far each one reaches',
      available: true
    },
    {
      label: 'Instant Support',
      path: `${base}/instant-support`,
      description: 'Your EAP plan, and how your people use it',
      available: availability?.instantSupport
    },
    {
      label: 'Salary Sacrifice',
      path: `${base}/salary-sacrifice`,
      description: 'Schemes on offer and who is enrolled',
      available: availability?.salarySacrifice
    },
    {
      label: 'Health Scheme',
      path: `${base}/health-scheme`,
      description: 'Cover on offer and who is enrolled',
      available: availability?.healthScheme
    },
    {
      label: 'Custom Perks',
      path: `${base}/custom`,
      description: 'The perks you added yourself, and what gets opened',
      available: availability?.customPerks
    },
    {
      label: isB2BFansMode ? 'Members' : 'Employees',
      path: `${base}/users`,
      description: `Per-${population.replace(/s$/, '')} perks records`,
      available: true
    }
  ].filter((page) => page.available);

  /**
   * Two redirects, both for people who arrive by URL rather than by clicking:
   * a company with no perks at all has no section to show, and a company that
   * has the section but not this programme would otherwise sit on a page whose
   * endpoint 403s.
   */
  useEffect(() => {
    if (loading || !availability) return;

    if (!availability.enabled) {
      navigate(`/${slug}/dashboard`, { replace: true });
      return;
    }

    /*
      Exact matches only. A prefix test would be useless here because the
      Overview tab's path IS the section root, so every sub-path would "match"
      it and nothing would ever redirect. The user-detail page has no tab of
      its own and is allowed through explicitly.
    */
    const allowedPaths = [...pages.map((page) => page.path), `${base}/user`];
    if (!allowedPaths.includes(location.pathname)) {
      navigate(base, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, availability, location.pathname]);

  if (loading) return <Loader />;
  if (!availability?.enabled) return null;

  return (
    <CompanySectionLayout
      title="Perks"
      subtitle={`The benefits you run for your ${population}, and who is actually using them.`}
      pages={pages.map(({ label, path, description }) => ({ label, path, description }))}
    >
      {/*
        The pages inside don't need availability passed down: the overview
        endpoint returns its own copy alongside the data, and every other page
        is only reachable when its programme is on.
      */}
      {children}
    </CompanySectionLayout>
  );
};

export default CompanyPerksLayout;
